import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Search, Clock, ChevronRight, BookOpen, ChevronDown,
  ShoppingCart, Store, Shield, HelpCircle, ExternalLink,
  Package, MapPin, Bot, Rocket, ScanBarcode, Globe,
  ArrowLeft, MessageCircle, Heart, Sparkles, X, Send,
  Loader2, Book, FileText, List, Film, ScrollText, AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  PORTAL_OVERVIEWS, 
  GLOSSARY, 
  RETAILER_ALL_DOCS,
  CONSUMER_ALL_DOCS,
  MULTILINGUAL_DOCS,
  searchDocumentation 
} from '../../data/documentationContent';
import { ADMIN_DOCS, CHARITY_DOCS } from '../../data/documentationExtended';
import { MultimediaSection } from './MultimediaSection';
import { ReleaseNotesSection, VersionBadge, DocVersionHistory } from './ReleaseNotes';
import { GovernanceSection } from './GovernanceSection';
import { DocFeedbackWidget, DocIssueReport, SuggestionForm } from './FeedbackSystem';
import { DeprecationNotice } from './DeprecationNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  ScanBarcode: ScanBarcode,
  Package: Package,
  ShoppingCart: ShoppingCart,
  Rocket: Rocket,
  Bot: Bot,
  MapPin: MapPin,
  Store: Store,
  Shield: Shield,
  Heart: Heart,
  Globe: Globe,
  BookOpen: BookOpen,
  BarChart3: Clock, // Using Clock as fallback
  Settings: HelpCircle, // Using HelpCircle as fallback
  TrendingUp: Sparkles, // Using Sparkles as fallback
  Hash: List, // Using List as fallback
  User: ExternalLink, // Using ExternalLink as fallback
  Award: Sparkles,
  Leaf: Heart, // Using Heart as fallback
  MessageSquare: MessageCircle,
  AlertTriangle: Clock, // Using Clock as fallback
  UserCheck: Shield, // Using Shield as fallback
  Activity: Clock,
  FileText: FileText
};

// Portal configuration
const PORTAL_CONFIG = {
  retailer: {
    name: 'Retailer',
    icon: Store,
    color: 'blue',
    bgColor: 'bg-blue-500',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    description: 'Post deals, manage orders, track performance'
  },
  consumer: {
    name: 'Consumer',
    icon: ShoppingCart,
    color: 'emerald',
    bgColor: 'bg-emerald-500',
    lightBg: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    description: 'Find deals, shop smart, save money'
  },
  admin: {
    name: 'Admin',
    icon: Shield,
    color: 'purple',
    bgColor: 'bg-purple-500',
    lightBg: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    description: 'Platform management & monitoring'
  },
  charity: {
    name: 'Charity',
    icon: Heart,
    color: 'rose',
    bgColor: 'bg-rose-500',
    lightBg: 'bg-rose-50',
    textColor: 'text-rose-600',
    borderColor: 'border-rose-200',
    description: 'Donations & impact tracking'
  }
};

export default function DocumentationHub({ userRole = 'consumer' }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activePortal, setActivePortal] = useState(userRole);
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [glossaryFilter, setGlossaryFilter] = useState('');
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  // Handle search
  useEffect(() => {
    if (searchQuery.length >= 2) {
      setIsSearching(true);
      const results = searchDocumentation(searchQuery, activePortal === 'all' ? null : activePortal);
      setSearchResults(results);
      setIsSearching(false);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, activePortal]);

  // AI Chat handler
  const handleAISearch = async () => {
    if (!aiQuery.trim()) return;
    
    setAiLoading(true);
    setAiResponse('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: `[Documentation Help Request - ${activePortal} portal] ${aiQuery}`,
          session_id: `docs-help-${Date.now()}`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.response || 'I can help you find information in our documentation. Try asking about specific features or workflows.');
      } else {
        setAiResponse('I apologize, but I encountered an issue. Please try searching the documentation directly or contact support.');
      }
    } catch (error) {
      console.error('AI search error:', error);
      setAiResponse('Unable to connect to the AI assistant. Please use the search function above or browse the documentation sections.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleBackToApp = () => {
    navigate(`/${userRole}/dashboard`);
  };

  // Get docs for active portal
  const getPortalDocs = () => {
    switch (activePortal) {
      case 'retailer':
        return RETAILER_ALL_DOCS;
      case 'consumer':
        return CONSUMER_ALL_DOCS;
      case 'admin':
        return ADMIN_DOCS;
      case 'charity':
        return CHARITY_DOCS;
      default:
        return {};
    }
  };

  // Get overview for active portal
  const getPortalOverview = () => {
    return PORTAL_OVERVIEWS[activePortal];
  };

  // Filter glossary
  const getFilteredGlossary = () => {
    if (!glossaryFilter) return Object.values(GLOSSARY);
    return Object.values(GLOSSARY).filter(term =>
      term.term.toLowerCase().includes(glossaryFilter.toLowerCase()) ||
      term.definition.toLowerCase().includes(glossaryFilter.toLowerCase()) ||
      term.category.toLowerCase().includes(glossaryFilter.toLowerCase())
    );
  };

  // Group glossary by category
  const getGroupedGlossary = () => {
    const filtered = getFilteredGlossary();
    const grouped = {};
    filtered.forEach(term => {
      if (!grouped[term.category]) {
        grouped[term.category] = [];
      }
      grouped[term.category].push(term);
    });
    return grouped;
  };

  const portalConfig = PORTAL_CONFIG[activePortal] || PORTAL_CONFIG.consumer;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="documentation-hub">
      {/* Header */}
      <div className={`bg-gradient-to-r from-${portalConfig.color}-600 to-${portalConfig.color}-500 text-white`}>
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={handleBackToApp}
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-testid="back-to-app-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to App
            </Button>
            <Badge variant="secondary" className="bg-white/20 text-white border-0">
              {portalConfig.name} Documentation
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Documentation Hub</h1>
              <p className="text-white/80 mt-1">Guides, tutorials, and answers for {portalConfig.name}s</p>
            </div>
          </div>

          {/* Global Search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
            <Input
              ref={searchInputRef}
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white focus:text-gray-900 focus:placeholder:text-gray-400"
              data-testid="doc-search-input"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute z-50 mt-2 max-w-2xl w-full bg-white rounded-lg shadow-xl border max-h-96 overflow-auto">
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-0"
                  onClick={() => {
                    setSearchQuery('');
                    if (result.portal !== 'shared') {
                      setActivePortal(result.portal);
                    }
                    if (result.docId) {
                      setExpandedDoc(result.docId);
                      setActiveSection('docs');
                    } else if (result.type === 'glossary') {
                      setActiveSection('glossary');
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="text-xs capitalize">
                      {result.type}
                    </Badge>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{result.title}</p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{result.description}</p>
                    </div>
                    {result.portal !== 'shared' && (
                      <Badge className={`${PORTAL_CONFIG[result.portal]?.lightBg} ${PORTAL_CONFIG[result.portal]?.textColor} text-xs`}>
                        {result.portal}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Portal Selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Object.entries(PORTAL_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            const isActive = activePortal === key;
            return (
              <Card
                key={key}
                className={`cursor-pointer transition-all ${
                  isActive 
                    ? `ring-2 ring-${config.color}-500 ${config.lightBg}` 
                    : 'hover:shadow-md'
                }`}
                onClick={() => {
                  setActivePortal(key);
                  setActiveSection('overview');
                  setExpandedDoc(null);
                }}
                data-testid={`portal-selector-${key}`}
              >
                <CardContent className="pt-4 pb-3 text-center">
                  <div className={`w-10 h-10 mx-auto ${config.lightBg} rounded-xl flex items-center justify-center mb-2`}>
                    <Icon className={`h-5 w-5 ${config.textColor}`} />
                  </div>
                  <p className={`font-medium text-sm ${isActive ? config.textColor : 'text-gray-700'}`}>
                    {config.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">{config.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeSection} onValueChange={setActiveSection}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" data-testid="tab-overview" className="gap-2">
              <Book className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-docs" className="gap-2">
              <FileText className="w-4 h-4" />
              Documentation
            </TabsTrigger>
            <TabsTrigger value="glossary" data-testid="tab-glossary" className="gap-2">
              <List className="w-4 h-4" />
              Glossary
            </TabsTrigger>
            <TabsTrigger value="ai-help" data-testid="tab-ai-help" className="gap-2">
              <Sparkles className="w-4 h-4" />
              AI Help
            </TabsTrigger>
            <TabsTrigger value="tutorials" data-testid="tab-tutorials" className="gap-2">
              <Film className="w-4 h-4" />
              Tutorials
            </TabsTrigger>
            <TabsTrigger value="governance" data-testid="tab-governance" className="gap-2">
              <ScrollText className="w-4 h-4" />
              Updates
            </TabsTrigger>
            {(activePortal === 'consumer' || activePortal === 'retailer') && (
              <TabsTrigger value="language" data-testid="tab-language" className="gap-2">
                <Globe className="w-4 h-4" />
                Language
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-0">
            <OverviewSection 
              portal={activePortal} 
              overview={getPortalOverview()} 
              config={portalConfig}
            />
          </TabsContent>

          {/* Documentation Tab */}
          <TabsContent value="docs" className="mt-0">
            <DocumentationSection
              docs={getPortalDocs()}
              expandedDoc={expandedDoc}
              setExpandedDoc={setExpandedDoc}
              config={portalConfig}
            />
          </TabsContent>

          {/* Glossary Tab */}
          <TabsContent value="glossary" className="mt-0">
            <GlossarySection
              glossaryFilter={glossaryFilter}
              setGlossaryFilter={setGlossaryFilter}
              groupedGlossary={getGroupedGlossary()}
            />
          </TabsContent>

          {/* AI Help Tab */}
          <TabsContent value="ai-help" className="mt-0">
            <AIHelpSection
              aiQuery={aiQuery}
              setAiQuery={setAiQuery}
              aiResponse={aiResponse}
              aiLoading={aiLoading}
              handleAISearch={handleAISearch}
              config={portalConfig}
            />
          </TabsContent>

          {/* Tutorials Tab */}
          <TabsContent value="tutorials" className="mt-0">
            <MultimediaSection 
              portal={activePortal}
              onNavigateToDoc={(docId) => {
                setExpandedDoc(docId);
                setActiveSection('docs');
              }}
            />
          </TabsContent>

          {/* Governance Tab */}
          <TabsContent value="governance" className="mt-0">
            <GovernanceTab 
              portal={activePortal}
              onNavigateToDoc={(docId) => {
                setExpandedDoc(docId);
                setActiveSection('docs');
              }}
            />
          </TabsContent>

          {/* Language Tab */}
          <TabsContent value="language" className="mt-0">
            <LanguageSection portal={activePortal} />
          </TabsContent>
        </Tabs>

        {/* Contact Support Footer */}
        <Card className={`mt-8 bg-gradient-to-r from-${portalConfig.color}-500 to-${portalConfig.color}-600 text-white border-0`}>
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h3 className="text-lg font-semibold mb-1">Still need help?</h3>
                <p className="opacity-90 text-sm">Our support team is here to assist you</p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" size="sm" className="gap-2">
                  <MessageCircle className="h-4 w-4" />
                  Live Chat
                </Button>
                <Button variant="secondary" size="sm" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Email Support
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Overview Section Component
function OverviewSection({ portal, overview, config }) {
  if (!overview) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-500">Documentation for {portal} portal coming soon.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Portal Header Card */}
      <Card className={`${config.lightBg} ${config.borderColor} border-2`}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${config.bgColor} rounded-xl flex items-center justify-center`}>
              <config.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl">{overview.title}</CardTitle>
              <CardDescription>{overview.description}</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="w-fit mt-3">
            <Globe className="w-3 h-3 mr-1" />
            {overview.languagePolicy}
          </Badge>
        </CardHeader>
      </Card>

      {/* Overview Sections */}
      <div className="grid gap-4">
        {overview.sections.map((section) => (
          <Card key={section.id} data-testid={`overview-section-${section.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{section.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <FormattedContent content={section.content} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Documentation Section Component
function DocumentationSection({ docs, expandedDoc, setExpandedDoc, config }) {
  const docList = Object.values(docs);

  if (docList.length === 0) {
    return (
      <Card className="p-8 text-center">
        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Documentation for this portal is coming soon.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick Nav */}
      <div className="flex flex-wrap gap-2 mb-4">
        {docList.map(doc => {
          const IconComponent = ICON_MAP[doc.icon] || FileText;
          return (
            <Badge
              key={doc.id}
              variant={expandedDoc === doc.id ? "default" : "outline"}
              className="cursor-pointer gap-1.5 py-1.5 px-3"
              onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}
            >
              <IconComponent className="w-3.5 h-3.5" />
              {doc.title.split(' ').slice(0, 2).join(' ')}
            </Badge>
          );
        })}
      </div>

      {/* Doc Cards */}
      {docList.map(doc => {
        const IconComponent = ICON_MAP[doc.icon] || FileText;
        const isExpanded = expandedDoc === doc.id;

        return (
          <Card 
            key={doc.id} 
            className={`transition-all ${isExpanded ? `ring-2 ring-${config.color}-500` : ''}`}
            data-testid={`doc-card-${doc.id}`}
          >
            <CardHeader 
              className="cursor-pointer" 
              onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 ${config.lightBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <IconComponent className={`w-5 h-5 ${config.textColor}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {doc.title}
                      <Badge variant="secondary" className="text-xs font-normal">
                        Priority {doc.priority}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">{doc.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {doc.estimatedTime}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="pt-0 border-t">
                {/* Sections */}
                <Accordion type="single" collapsible className="mt-4">
                  {doc.sections.map((section, idx) => (
                    <AccordionItem key={section.id} value={section.id}>
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full ${config.lightBg} ${config.textColor} flex items-center justify-center text-xs font-medium`}>
                            {idx + 1}
                          </span>
                          {section.title}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="prose prose-sm max-w-none pl-8">
                          <FormattedContent content={section.content} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* FAQs */}
                {doc.faqs && doc.faqs.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-gray-500" />
                      Frequently Asked Questions
                    </h4>
                    <Accordion type="single" collapsible>
                      {doc.faqs.map((faq, idx) => (
                        <AccordionItem key={idx} value={`faq-${idx}`}>
                          <AccordionTrigger className="text-left text-sm hover:no-underline">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// Glossary Section Component
function GlossarySection({ glossaryFilter, setGlossaryFilter, groupedGlossary }) {
  const categories = Object.keys(groupedGlossary).sort();

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search glossary..."
          value={glossaryFilter}
          onChange={(e) => setGlossaryFilter(e.target.value)}
          className="pl-10"
          data-testid="glossary-search"
        />
      </div>

      {categories.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500">No terms found matching "{glossaryFilter}"</p>
        </Card>
      ) : (
        categories.map(category => (
          <Card key={category}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{category}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {groupedGlossary[category].map(term => (
                  <div 
                    key={term.term} 
                    className="p-3 bg-gray-50 rounded-lg"
                    data-testid={`glossary-term-${term.term.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">{term.term}</h4>
                        <p className="text-sm text-gray-600 mt-1">{term.definition}</p>
                      </div>
                    </div>
                    {term.relatedTerms && term.relatedTerms.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-gray-500">Related:</span>
                        {term.relatedTerms.map(related => (
                          <Badge 
                            key={related} 
                            variant="outline" 
                            className="text-xs cursor-pointer hover:bg-gray-100"
                            onClick={() => setGlossaryFilter(related)}
                          >
                            {related}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// AI Help Section Component
function AIHelpSection({ aiQuery, setAiQuery, aiResponse, aiLoading, handleAISearch, config }) {
  return (
    <div className="space-y-6">
      <Card className={`${config.lightBg} border-2 ${config.borderColor}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className={`w-5 h-5 ${config.textColor}`} />
            AI Documentation Assistant
          </CardTitle>
          <CardDescription>
            Ask questions about DealShaq features, workflows, or troubleshooting
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Ask anything about DealShaq..."
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
              className="flex-1"
              data-testid="ai-help-input"
            />
            <Button 
              onClick={handleAISearch} 
              disabled={aiLoading || !aiQuery.trim()}
              className={config.bgColor}
              data-testid="ai-help-submit"
            >
              {aiLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Example Questions */}
          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {[
                "How do I post a deal?",
                "What are the discount levels?",
                "How does order acknowledgment work?",
                "What is N-DUSGF?"
              ].map((q, idx) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => setAiQuery(q)}
                >
                  {q}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Response */}
      {aiResponse && (
        <Card data-testid="ai-response">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Response
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <FormattedContent content={aiResponse} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Language Section Component
function LanguageSection({ portal }) {
  const languageDocs = MULTILINGUAL_DOCS[portal];

  if (!languageDocs) {
    return (
      <Card className="p-8 text-center">
        <Globe className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Language documentation not available for this portal.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className={portal === 'consumer' ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {languageDocs.title}
          </CardTitle>
          <CardDescription>{languageDocs.description}</CardDescription>
        </CardHeader>
      </Card>

      {languageDocs.sections.map(section => (
        <Card key={section.id} data-testid={`language-section-${section.id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{section.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <FormattedContent content={section.content} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Governance Tab Component
function GovernanceTab({ portal, onNavigateToDoc }) {
  const [activeView, setActiveView] = useState('release-notes');
  const [showIssueReport, setShowIssueReport] = useState(false);
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);

  return (
    <div className="space-y-6" data-testid="governance-tab">
      {/* Sub-navigation */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeView === 'release-notes' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('release-notes')}
          className="gap-2"
        >
          <ScrollText className="w-4 h-4" />
          Release Notes
        </Button>
        <Button
          variant={activeView === 'governance' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('governance')}
          className="gap-2"
        >
          <Shield className="w-4 h-4" />
          Governance
        </Button>
        <Button
          variant={activeView === 'feedback' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('feedback')}
          className="gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          Feedback
        </Button>
      </div>

      {/* Release Notes View */}
      {activeView === 'release-notes' && (
        <ReleaseNotesSection onNavigateToDoc={onNavigateToDoc} />
      )}

      {/* Governance View */}
      {activeView === 'governance' && (
        <GovernanceSection />
      )}

      {/* Feedback View */}
      {activeView === 'feedback' && (
        <div className="space-y-6">
          {/* Header */}
          <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <MessageCircle className="w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Help Us Improve</h2>
                  <p className="text-white/80">Your feedback shapes our documentation</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick feedback */}
          <DocFeedbackWidget 
            docId="general" 
            docTitle="Documentation Hub"
            portal={portal}
          />

          {/* Action buttons */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowIssueReport(true)}>
              <CardContent className="py-6 text-center">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-1">Report an Issue</h3>
                <p className="text-sm text-gray-600">Found outdated or incorrect information?</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowSuggestionForm(true)}>
              <CardContent className="py-6 text-center">
                <Sparkles className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-1">Make a Suggestion</h3>
                <p className="text-sm text-gray-600">Request new content or improvements</p>
              </CardContent>
            </Card>
          </div>

          {/* Feedback summary */}
          <Card className="bg-gray-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">How Feedback Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3 text-center">
                <div className="p-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2 text-indigo-700 font-bold">1</div>
                  <p className="text-sm font-medium">Submit</p>
                  <p className="text-xs text-gray-500">Share your feedback</p>
                </div>
                <div className="p-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2 text-indigo-700 font-bold">2</div>
                  <p className="text-sm font-medium">Review</p>
                  <p className="text-xs text-gray-500">Our team evaluates</p>
                </div>
                <div className="p-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2 text-indigo-700 font-bold">3</div>
                  <p className="text-sm font-medium">Improve</p>
                  <p className="text-xs text-gray-500">Updates are published</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modals */}
      <DocIssueReport
        docId="general"
        docTitle="Documentation Hub"
        portal={portal}
        isOpen={showIssueReport}
        onClose={() => setShowIssueReport(false)}
      />

      <SuggestionForm
        portal={portal}
        isOpen={showSuggestionForm}
        onClose={() => setShowSuggestionForm(false)}
      />
    </div>
  );
}

// Formatted Content Component - Handles markdown-like formatting
function FormattedContent({ content }) {
  if (!content) return null;

  // Split content into lines and process
  const lines = content.split('\n');
  const elements = [];
  let currentList = null;
  let currentTable = null;
  let inCodeBlock = false;
  let codeContent = '';

  lines.forEach((line, idx) => {
    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${idx}`} className="bg-gray-100 p-3 rounded-lg text-sm overflow-x-auto">
            <code>{codeContent}</code>
          </pre>
        );
        codeContent = '';
      }
      inCodeBlock = !inCodeBlock;
      return;
    }

    if (inCodeBlock) {
      codeContent += line + '\n';
      return;
    }

    // Tables
    if (line.startsWith('|')) {
      if (!currentTable) {
        currentTable = [];
      }
      const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
      if (!cells.every(c => c.match(/^-+$/))) {
        currentTable.push(cells);
      }
      return;
    } else if (currentTable) {
      elements.push(
        <div key={`table-${idx}`} className="overflow-x-auto my-3">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100">
                {currentTable[0]?.map((cell, i) => (
                  <th key={i} className="border px-3 py-2 text-left font-medium">{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentTable.slice(1).map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} className="border px-3 py-2">
                      <InlineFormatted text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      currentTable = null;
    }

    // Headers
    if (line.startsWith('**') && line.endsWith('**')) {
      if (currentList) {
        elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
        currentList = null;
      }
      elements.push(
        <h4 key={idx} className="font-semibold text-gray-900 mt-4 mb-2">
          {line.replace(/\*\*/g, '')}
        </h4>
      );
      return;
    }

    // List items
    if (line.startsWith('- ') || line.startsWith('• ')) {
      if (!currentList) currentList = [];
      const text = line.replace(/^[-•]\s*/, '');
      currentList.push(
        <li key={idx} className="text-gray-700">
          <InlineFormatted text={text} />
        </li>
      );
      return;
    }

    // Numbered list
    if (line.match(/^\d+\.\s/)) {
      if (!currentList) currentList = [];
      const text = line.replace(/^\d+\.\s*/, '');
      currentList.push(
        <li key={idx} className="text-gray-700">
          <InlineFormatted text={text} />
        </li>
      );
      return;
    }

    // Regular paragraph
    if (line.trim()) {
      if (currentList) {
        elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
        currentList = null;
      }
      elements.push(
        <p key={idx} className="text-gray-700 mb-2">
          <InlineFormatted text={line} />
        </p>
      );
    }
  });

  // Handle remaining list
  if (currentList) {
    elements.push(<ul key="list-final" className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
  }

  return <>{elements}</>;
}

// Inline formatting (bold, code, branded-flame img, etc.)
// Whitelist-only HTML support: only `<img src="/branded-flame.svg" ...>` tags
// are rendered as real images. Everything else stays as escaped text (XSS-safe).
const FLAME_IMG_RE = /<img\s+src=["']\/branded-flame\.svg["'][^>]*>/gi;

function renderWithFlames(text, keyPrefix) {
  const pieces = [];
  let lastIdx = 0;
  let match;
  const re = new RegExp(FLAME_IMG_RE.source, 'gi');
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) {
      pieces.push(text.slice(lastIdx, match.index));
    }
    pieces.push(
      <img
        key={`${keyPrefix}-flame-${match.index}`}
        src="/branded-flame.svg"
        alt="DealShaq"
        width={14}
        height={14}
        className="inline-block align-text-bottom"
        draggable={false}
      />
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) pieces.push(text.slice(lastIdx));
  return pieces.length ? pieces : [text];
}

function InlineFormatted({ text }) {
  // First, split on branded-flame img tokens so we can render them as JSX
  const flameParts = renderWithFlames(text, 'if');
  return (
    <>
      {flameParts.map((part, pIdx) => {
        if (typeof part !== 'string') return part; // already a React element (flame img)
        // Handle bold
        const parts = part.split(/(\*\*[^*]+\*\*)/g);
        return (
          <React.Fragment key={`p-${pIdx}`}>
            {parts.map((sub, idx) => {
              if (sub.startsWith('**') && sub.endsWith('**')) {
                return <strong key={`${pIdx}-${idx}`}>{sub.slice(2, -2)}</strong>;
              }
              // Handle inline code
              const codeParts = sub.split(/(`[^`]+`)/g);
              return codeParts.map((codePart, codeIdx) => {
                if (codePart.startsWith('`') && codePart.endsWith('`')) {
                  return <code key={`${pIdx}-${idx}-${codeIdx}`} className="bg-gray-100 px-1 rounded text-sm">{codePart.slice(1, -1)}</code>;
                }
                return <span key={`${pIdx}-${idx}-${codeIdx}`}>{codePart}</span>;
              });
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}
